import { useEffect, useMemo, useRef, useState } from 'react'
import { LogIn, Send, Wifi, WifiOff } from 'lucide-react'
import { Card } from '../../../components/ui/Card'
import { Button } from '../../../components/ui/Button'
import { safeStorageGet, safeStorageSet } from '../../../utils/storage'
import { loadSession } from '../../../auth/state'

const DEVICE_ID_KEY = 'anipin.preview.ai.deviceId.v1'

function randomId() {
  try {
    return crypto.randomUUID()
  } catch {
    return `u_${Date.now()}_${Math.random().toString(16).slice(2)}`
  }
}

function getOrCreateDeviceId() {
  const existing = String(safeStorageGet(DEVICE_ID_KEY) || '').trim()
  if (existing) return existing
  const next = `app-preview-${randomId().slice(0, 10)}`
  safeStorageSet(DEVICE_ID_KEY, next)
  return next
}

function formatTime(ts) {
  try {
    return new Date(ts || Date.now()).toLocaleTimeString()
  } catch {
    return ''
  }
}

function Bubble({ dir, text, time }) {
  const isOut = dir === 'out'
  const wrap = isOut ? 'justify-end' : 'justify-start'
  const bubble = isOut
    ? 'bg-brand-pink text-white shadow-[4px_4px_0px_0px_#E04848]'
    : 'bg-white dark:bg-slate-950 text-brand-textDark dark:text-slate-100 shadow-[4px_4px_0px_0px_#40E0D0]'

  return (
    <div className={`flex ${wrap}`}>
      <div className={`max-w-[86%] rounded-3xl ${isOut ? 'rounded-br-xl' : 'rounded-bl-xl'} border-2 border-brand-textDark/80 dark:border-slate-700 px-4 py-3 ${bubble}`}>
        <div className="whitespace-pre-wrap break-words text-sm font-extrabold leading-relaxed">{text}</div>
        {time ? <div className={`mt-1 text-[11px] font-mono ${isOut ? 'text-white/80' : 'text-brand-text/70 dark:text-slate-300/70'}`}>[{time}]</div> : null}
      </div>
    </div>
  )
}

export default function AppPreviewAi() {
  const session = useMemo(() => loadSession(), [])
  const isAuthed = Boolean(session?.tokens?.accessToken)

  const deviceId = useMemo(() => getOrCreateDeviceId(), [])

  const [wsConnected, setWsConnected] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [items, setItems] = useState([])
  const [input, setInput] = useState('')
  const endRef = useRef(null)
  const wsRef = useRef(null)
  const manualCloseRef = useRef(false)

  const wsUrl = useMemo(() => {
    const proto = window.location.protocol === 'https:' ? 'wss' : 'ws'
    const base = `${proto}://${window.location.host}/chamii/v1/`
    const qs = new URLSearchParams()
    qs.set('device-id', deviceId)
    qs.set('client-id', 'app-preview')
    return `${base}?${qs.toString()}`
  }, [deviceId])

  const addSys = (text) => setItems((prev) => [...prev.slice(-199), { dir: 'sys', text: String(text || ''), time: formatTime() }])

  const connect = async () => {
    if (wsRef.current && wsConnected) return
    manualCloseRef.current = false
    setConnecting(true)
    addSys('正在连接云端…')

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        setWsConnected(true)
        setConnecting(false)
        addSys('已连接。随便聊聊吧～')
        try {
          ws.send(JSON.stringify({ type: 'history' }))
        } catch {}
      }

      ws.onclose = () => {
        setWsConnected(false)
        setConnecting(false)
        wsRef.current = null
        if (!manualCloseRef.current) addSys('连接断开了，我会自动重连。')
      }

      ws.onerror = () => {
        // onclose will follow
      }

      ws.onmessage = (event) => {
        const text = typeof event.data === 'string' ? event.data : ''
        if (!text) return
        let msg = null
        try {
          msg = JSON.parse(text)
        } catch {
          msg = null
        }
        const type = String(msg?.type || '').trim()
        if (type === 'history') {
          const list = Array.isArray(msg?.items) ? msg.items : []
          const mapped = list
            .map((it) => {
              const role = String(it?.role || '').trim()
              const content = String(it?.content || '').trim()
              if (!content) return null
              return { dir: role === 'user' ? 'out' : 'in', text: content, time: '' }
            })
            .filter(Boolean)
          if (mapped.length) {
            setItems((prev) => [...mapped, ...prev.filter((x) => x?.dir === 'sys').slice(-8)])
            addSys('已恢复聊天记录。')
          }
          return
        }
        if (type === 'conversation') {
          const role = String(msg?.role || '').trim()
          const content = String(msg?.text || '').trim()
          if (!content) return
          const time = formatTime(msg?.timestamp)
          if (role === 'user') setItems((prev) => [...prev.slice(-199), { dir: 'out', text: content, time }])
          else setItems((prev) => [...prev.slice(-199), { dir: 'in', text: content, time }])
          return
        }
        if (type === 'error') {
          addSys(String(msg?.error || '发生错误'))
          return
        }
      }
    } catch (e) {
      setConnecting(false)
      setWsConnected(false)
      wsRef.current = null
      addSys(`连接失败：${String(e?.message || e)}`)
    }
  }

  const disconnect = () => {
    manualCloseRef.current = true
    try {
      wsRef.current?.close()
    } catch {}
    wsRef.current = null
    setWsConnected(false)
    setConnecting(false)
  }

  const send = async () => {
    const text = String(input || '').trim()
    if (!text) return

    if (!isAuthed) {
      setItems((prev) => [
        ...prev.slice(-199),
        { dir: 'in', text: '先登录一下我就能更好地记住你啦～（右上角点 Login）', time: formatTime() },
      ])
      setInput('')
      return
    }

    if (!wsConnected || !wsRef.current) {
      addSys('我现在还没连上云端，等我一下下～')
      await connect()
      return
    }

    setInput('')

    try {
      wsRef.current.send(JSON.stringify({ type: 'chat_cloud', text, mode: 'manual' }))
    } catch (e) {
      addSys(`发送失败：${String(e?.message || e)}`)
    }
  }

  useEffect(() => {
    connect()
    return () => disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [items])

  const statusLabel = wsConnected ? 'Online' : connecting ? 'Connecting…' : 'Offline'
  const dotClass = wsConnected ? 'bg-emerald-400' : 'bg-rose-500'

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative">
              <img src="/chamii-logo-icon-only.png" alt="Chamii" className="w-11 h-11 rounded-2xl border-2 border-brand-textDark bg-white object-contain" />
              <span className={`absolute -right-0.5 -bottom-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${dotClass} animate-pulse`} />
            </div>
            <div className="min-w-0">
              <div className="font-black leading-tight truncate">Chamii AI</div>
              <div className="text-xs font-bold text-brand-text/70 dark:text-slate-300/70 truncate">
                {wsConnected ? <Wifi className="inline w-3.5 h-3.5 mr-1" /> : <WifiOff className="inline w-3.5 h-3.5 mr-1" />}
                {statusLabel}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {!isAuthed ? (
              <Button as="a" href="/login?next=/app/ai" variant="secondary" size="sm">
                <LogIn className="w-4 h-4" />
                Login
              </Button>
            ) : null}

            {wsConnected ? (
              <Button variant="neutral" size="sm" onClick={disconnect}>
                Disconnect
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={connect} disabled={connecting}>
                {connecting ? 'Connecting…' : 'Connect'}
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="space-y-3">
        {items.map((m, idx) => {
          if (m.dir === 'sys') {
            return (
              <div key={idx} className="flex justify-center">
                <div className="max-w-[92%] px-3 py-1.5 rounded-full bg-brand-bg dark:bg-slate-900/30 border-2 border-brand-textDark shadow-[4px_4px_0px_0px_#E5BC2E] text-xs font-extrabold text-brand-textDark/80 dark:text-slate-200">
                  <span className="text-brand-text/60 dark:text-slate-300/60 font-mono mr-2">[{m.time}]</span>
                  {m.text}
                </div>
              </div>
            )
          }
          return <Bubble key={idx} dir={m.dir} text={m.text} time={m.time} />
        })}
        <div ref={endRef} />
      </div>

      <Card className="p-4">
        {!isAuthed ? (
          <div className="text-sm font-extrabold text-brand-textDark/80 dark:text-slate-200">
            先登录一下就能继续聊天啦～（不会跳外部浏览器，会走系统登录）
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              rows={2}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={wsConnected ? '输入一句话，回车发送…' : '我还在连接中…'}
              className="flex-1 resize-none rounded-3xl border-2 border-brand-textDark/20 dark:border-slate-700 bg-white dark:bg-slate-950 px-4 py-3 text-sm font-extrabold outline-none focus:border-brand-cyan focus:ring-2 focus:ring-brand-cyan/20"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
            />
            <Button variant="primary" size="lg" className="shrink-0 px-4" onClick={send} disabled={!wsConnected || !input.trim()}>
              <Send className="w-5 h-5" />
            </Button>
          </div>
        )}
      </Card>
    </div>
  )
}
